import { createSelector, createSlice } from '@reduxjs/toolkit'
import {
  baseReducers,
  getIdOrModelId,
  SBAPI,
  SBSelectRaw,
  SBState,
} from '@supplement-bacon/react-toolkit/utils'
import { initialQuery, TableParams } from '@supplement-bacon/sb-table-filters'
import { Cluster } from '../models/Cluster'
import { clusterSchema } from '../models/schema'
import {
  createCluster as createClusterAPI,
  deleteCluster as deleteClusterAPI,
  fetchClusterById,
  fetchClusters,
  updateCluster as updateClusterAPI,
} from '../services/ClusterService'
import { AppDispatch, RootState } from './store'

const initialState: SBState<Cluster> = {
  isLoading: false,
  error: null,
  items: {},
  ids: [],
  selectedId: undefined,
  query: initialQuery,
}

const slice = createSlice({
  name: 'cluster',
  initialState,
  reducers: baseReducers,
})

// Reducer
export default slice.reducer
export const {
  getItemsSuccess: getClustersSuccess,
  setQuery: setClusterQuery,
  reset: resetClusterState,
  resetQueryAndIds: resetClusterQueryAndIds,
} = slice.actions

/**
 * Selectors
 */

const selectRawItems: SBSelectRaw<RootState, { [key: number]: Cluster }> = (
  state: RootState
) => state[slice.name].items
const selectRawIds: SBSelectRaw<RootState, number[]> = (state: RootState) =>
  state[slice.name].ids
const selectRawSelectedId: SBSelectRaw<RootState, number | undefined> = (
  state: RootState
) => state[slice.name].selectedId

export const selectClusters = () =>
  createSelector(
    [selectRawItems, selectRawIds],
    (items, ids) => ids.map((id) => items[id]).filter((i) => i)
    // Filter allow to return only non-null elements
  )
export const selectSelectedCluster = () =>
  createSelector([selectRawItems, selectRawSelectedId], (items, id) =>
    id !== undefined ? items[id] : undefined
  )
export const selectClusterById = (id: number) =>
  createSelector([selectRawItems], (items) =>
    items.hasOwnProperty(id) ? items[id] : undefined
  )
export const selectClustersByIds = (ids: number[]) =>
  createSelector([selectRawItems], (items) =>
    ids.filter((id) => items.hasOwnProperty(id)).map((id) => items[id])
  )

/**
 * Actions
 */

export const setSelectedCluster =
  (cluster: Cluster | number) => async (dispatch: AppDispatch) =>
    dispatch(slice.actions.setSelectedId(getIdOrModelId<Cluster>(cluster)))

export const getClusters = (params: TableParams) =>
  SBAPI.FetchPaginatedDispatched(
    fetchClusters(params),
    [clusterSchema],
    slice.actions
  )

export const createCluster = (cluster: Cluster) =>
  SBAPI.CreateDispatched(
    createClusterAPI(cluster),
    clusterSchema,
    slice.actions
  )

export const getClusterWithId = (id: number) =>
  SBAPI.FetchDispatched<Cluster>(
    fetchClusterById(id),
    clusterSchema,
    slice.actions
  )

export const updateCluster = (cluster: Cluster) =>
  SBAPI.UpdateDispatched<Cluster>(
    updateClusterAPI(cluster),
    cluster,
    slice.actions
  )

export const deleteCluster = (cluster: Cluster) =>
  SBAPI.DestroyDispatched<Cluster>(
    deleteClusterAPI(cluster),
    cluster,
    slice.actions
  )
